import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { supabase } from "../lib/supabaseClient";
import { JUNGNA_RPC, JUNGNA_TABLES } from "../lib/jungnaTables";
import { resolveProductBank } from "../lib/productBank";

// Helper function to check if a string is a valid UUID
function isValidUUID(uuid: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

export interface ProductInfo {
  title: string;
  description: string;
  price: string;
  topCategory: string;
  bottomCategory: string;
  status: string;
  sellerName: string;
  sellerContact: string;
  content: string;
  date: string;
  views: number;
}

export interface OrderSummaryInfo {
  shippingLabel: string;
}

export interface TotalSummaryInfo {
  totalAmount: string;
  selectedPaymentMethod: string;
}

export interface SellerInfo {
  name: string;
  contact: string;
  reviews: {
    positive: number;
    neutral: number;
    negative: number;
  };
}

export interface OrderCompleteInfo {
  productName: string;
  price: string;
  bankName: string;
  accountNumber: string;
}

export interface BankInfo {
  bankName: string | null;
  accountNumber: string | null;
}

export interface ProductContextProps {
  productInfo: ProductInfo;
  setProductInfo: React.Dispatch<React.SetStateAction<ProductInfo>>;
  orderSummary: OrderSummaryInfo;
  setOrderSummary: React.Dispatch<React.SetStateAction<OrderSummaryInfo>>;
  totalSummary: TotalSummaryInfo;
  setTotalSummary: React.Dispatch<React.SetStateAction<TotalSummaryInfo>>;
  sellerInfo: SellerInfo;
  setSellerInfo: React.Dispatch<React.SetStateAction<SellerInfo>>;
  orderCompleteInfo: OrderCompleteInfo;
  setOrderCompleteInfo: React.Dispatch<React.SetStateAction<OrderCompleteInfo>>;
  productImage: string;
  setProductImage: React.Dispatch<React.SetStateAction<string>>;
  productImages: string[];
  setProductImages: React.Dispatch<React.SetStateAction<string[]>>;
  isLoading: boolean;
  currentProductId: string | null;
  setCurrentProductId: React.Dispatch<React.SetStateAction<string | null>>;
  loadProductData: (productId: string) => Promise<void>;
  bankInfo: BankInfo | null;
  setBankInfo: React.Dispatch<React.SetStateAction<BankInfo | null>>;
}

export const ProductContext = createContext<ProductContextProps | undefined>(
  undefined
);

export const ProductProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [productInfo, setProductInfo] = useState<ProductInfo>({
    title: "기본",
    description: "제목",
    price: "상품가격",
    topCategory: "홈 > 카테고리 분류 > 카테고리",
    bottomCategory: "[태그] 태그/태그/태그",
    status: "판매(안전)",
    sellerName: "-",
    sellerContact: "이메일주소 ㅣ 전화번호",
    content: "상품 설명", // 초기값 설정
    date: "2024.11.07 21:46", // 초기값 설정
    views: 17, // 초기값 설정
  });

  const [orderSummary, setOrderSummary] = useState<OrderSummaryInfo>({
    shippingLabel: "무료배송",
  });

  const [totalSummary, setTotalSummary] = useState<TotalSummaryInfo>({
    totalAmount: "총 결제금액",
    selectedPaymentMethod: "무통장입금",
  });

  const [sellerInfo, setSellerInfo] = useState<SellerInfo>({
    name: "판매자 닉네임",
    contact: "de*****@naver.com ㅣ 안심번호 이용중",
    reviews: {
      positive: 37,
      neutral: 1,
      negative: 0,
    },
  });

  const [orderCompleteInfo, setOrderCompleteInfo] = useState<OrderCompleteInfo>(
    {
      productName: "주문완료 페이지 상품 제목",
      price: "상품가격",
      bankName: "우리은행",
      accountNumber: " 123-123-123456",
    }
  );

  const [productImage, setProductImage] = useState<string>(
    "https://i.imgur.com/dEcgxWl.png"
  );
  const [productImages, setProductImages] = useState<string[]>([]);

  const [currentProductId, setCurrentProductId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [bankInfo, setBankInfo] = useState<BankInfo | null>(null);

  const loadProductData = useCallback(async (productId: string) => {
    console.log(`loadProductData 호출됨: ${productId}`);
    setIsLoading(true);
    try {
      console.log(`상품 데이터 로드 시작: ${productId}`);
      let productData: any = null;
      let productError: any = null;

      // 1. UUID로 상품 조회 시도
      if (isValidUUID(productId)) {
        console.log(`Supabase 쿼리 실행: products.id = ${productId}`);
        const { data, error } = await supabase
          .from(JUNGNA_TABLES.products)
          .select("*") // 기본 상품 정보만 가져옴
          .eq("id", productId)
          .single();
        productData = data;
        productError = error;
        console.log("UUID 쿼리 결과:", { data, error });
      }

      // 2. Slug로 상품 조회 시도 (UUID 실패 시 또는 UUID가 아닐 경우)
      if (!productData && !isValidUUID(productId)) {
        console.log(`Supabase 쿼리 실행: products.slug = ${productId}`);
        const { data, error } = await supabase
          .from(JUNGNA_TABLES.products)
          .select("*")
          .eq("slug", productId)
          .single();
        productData = data;
        productError = error;
        console.log("Slug 쿼리 결과:", { data, error });
      }

      // 3. URL 매핑으로 상품 조회 시도 (UUID/Slug 실패 시 또는 ID가 URL 형태일 경우)
      if (!productData && productId.startsWith("/")) {
        console.log(`Supabase 쿼리 실행: url_mappings.url = /${productId}`);
        const { data: mappingData } = await supabase
          .from(JUNGNA_TABLES.urlMappings)
          .select("product_id")
          .eq("url", `/${productId}`)
          .eq("is_active", true)
          .single();

        if (mappingData?.product_id) {
          console.log("URL 매핑으로 product_id 찾음:", mappingData.product_id);
          const { data, error } = await supabase
            .from(JUNGNA_TABLES.products)
            .select("*")
            .eq("id", mappingData.product_id)
            .single();
          productData = data;
          productError = error;
          console.log("URL 매핑 -> Product 쿼리 결과:", { data, error });
        }
      }

      // 상품 조회 최종 확인
      if (productData) {
        console.log("상품 조회 성공:", productData);

        // 4. url_mappings 테이블에서 seller_id 조회
        let sellerData: any = null;
        console.log(`Supabase 쿼리 실행: url_mappings.product_id = ${productData.id}`);
        const { data: mappingInfo, error: mappingError } = await supabase
          .from(JUNGNA_TABLES.urlMappings)
          .select("seller_id, bank_id") // bank_id도 함께 조회
          .eq("product_id", productData.id)
          .eq("is_active", true)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(); // 상품에 매핑이 없을 수도 있음

        console.log("URL Mapping 조회 결과:", { data: mappingInfo, error: mappingError });

        // 5. 판매자 정보 조회 (seller_id가 있는 경우)
        if (mappingInfo?.seller_id) {
          console.log(`URL 매핑에서 seller_id 찾음: ${mappingInfo.seller_id}`);
          console.log(`Supabase 쿼리 실행: sellers.id = ${mappingInfo.seller_id}`);
          const { data: sellerInfoResult, error: sellerError } = await supabase
            .from(JUNGNA_TABLES.sellers)
            .select("*") // 필요한 모든 판매자 정보
            .eq("id", mappingInfo.seller_id)
            .single();

          console.log("Seller 조회 결과:", { data: sellerInfoResult, error: sellerError });
          if (sellerInfoResult) {
            sellerData = sellerInfoResult;
          } else {
            console.error("Seller 조회 실패 또는 없음:", sellerError);
          }
        } else {
          console.log("URL 매핑에서 seller_id를 찾지 못했거나 매핑 정보 없음:", mappingError);
        }

        // 6. 상품에 연결된 은행 정보 조회
        const resolvedBank = await resolveProductBank(productData.id);
        console.log("Bank 조회 결과:", resolvedBank);
        if (resolvedBank) {
          setBankInfo({
            bankName: resolvedBank.name || null,
            accountNumber: resolvedBank.account_number || null,
          });

          setOrderCompleteInfo({
            productName: productData.title || "상품명 없음",
            price: productData.price || "0",
            bankName: resolvedBank.name || "은행 정보 없음",
            accountNumber: resolvedBank.account_number || "",
          });
        } else {
          console.log("상품에 연결된 활성 은행 정보를 찾지 못했습니다.");
          setBankInfo(null);
        }

        // 7. 기본 상품 데이터 처리 (은행 정보는 이미 위에서 처리함)
        // OrderCompleteInfo가 은행 정보와 함께 이미 설정되었으므로 여기서는 제외
        setProductInfo({
          title: productData.title || "제목 없음",
          description: productData.description || "",
          price: productData.price || "가격 정보 없음",
          topCategory: productData.top_category || "",
          bottomCategory: productData.bottom_category || "",
          status: productData.status || "판매중",
          sellerName: sellerData?.name || "",
          sellerContact: sellerData?.contact || "",
          content: productData.content || "",
          date: productData.created_at || new Date().toISOString(),
          views: productData.views || 0,
        });

        // 판매자 정보 설정
        if (sellerData) {
          setSellerInfo({
            name: sellerData.name || "판매자 정보 없음",
            contact: sellerData.contact || "",
            reviews: {
              positive: sellerData.positive_reviews || 0,
              neutral: sellerData.neutral_reviews || 0,
              negative: sellerData.negative_reviews || 0,
            },
          });
        } else {
          // 판매자 정보 없음 상태로 설정
          setSellerInfo({
            name: "판매자 정보 없음",
            contact: "",
            reviews: {
              positive: 0,
              neutral: 0,
              negative: 0,
            },
          });
        }

        // TotalSummary 설정
        setTotalSummary((prev) => ({
          ...prev,
          totalAmount: productData.price || "0",
        }));

        // 상품 이미지 설정 (대표)
        setProductImage(productData.image_url || "https://i.imgur.com/dEcgxWl.png");
        // 멀티 이미지 로드
        try {
          const { data: imgs, error: imgsErr } = await supabase
            .from(JUNGNA_TABLES.productImages)
            .select("public_url, storage_path, position, created_at")
            .eq("product_id", productData.id)
            .order("position", { ascending: true });
          if (!imgsErr) {
            const urls = (imgs || [])
              .map((i: any) => i.public_url)
              .filter((u: string | null) => !!u) as string[];
            setProductImages(urls);
          }
        } catch (e) {
          console.warn("product_images 로드 실패", e);
          setProductImages([]);
        }

        // 조회수 증가 처리는 기존과 동일하게 유지
        if (productData.id) {
          try {
            supabase
              .rpc(JUNGNA_RPC.incrementProductViews, {
                product_id_input: productData.id,
              })
              .then(({ error: updateError }) => {
                if (updateError) console.error("조회수 업데이트 실패:", updateError);
                else console.log(`조회수 업데이트 성공: ${productData.id}`);
              });
          } catch (updateError) {
            console.error("조회수 업데이트 중 동기 오류:", updateError);
          }
        }

        return; // 성공적으로 처리 완료

      } else {
        // 모든 방법으로 상품 조회 실패
        console.error("상품을 찾을 수 없습니다:", productId, productError);
        throw new Error(`상품을 찾을 수 없습니다: ${productError?.message || "Unknown error"}`);
      }

    } catch (error) {
      console.error("상품 데이터 로드 중 오류 발생:", error);
      setIsLoading(false);
      // 에러 상태를 설정하거나 상위로 전파할 수 있음
      // 예: setErrorState(error); 또는 throw error;
      throw error; // 에러를 상위로 전파하여 컴포넌트에서 처리
    } finally {
      setIsLoading(false);
      console.log("상품 데이터 로드 로직 완료");
    }
  }, []);

  // 현재 상품 ID가 변경될 때 데이터 로드
  useEffect(() => {
    if (currentProductId) {
      loadProductData(currentProductId).catch((error) => {
        console.error("상품 데이터 로드 실패:", error);
      });
    }
  }, [currentProductId, loadProductData]);

  return (
    <ProductContext.Provider
      value={{
        productInfo,
        setProductInfo,
        orderSummary,
        setOrderSummary,
        totalSummary,
        setTotalSummary,
        sellerInfo,
        setSellerInfo,
        orderCompleteInfo,
        setOrderCompleteInfo,
        productImage,
        setProductImage,
        productImages,
        setProductImages,
        isLoading,
        currentProductId,
        setCurrentProductId,
        loadProductData,
        bankInfo,
        setBankInfo,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
